import { useState, useCallback, memo, useRef, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  CounterBadge,
  mergeClasses,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  AlertRegular,
  AlertFilled,
  DismissRegular,
  DeleteRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  InfoRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from '@fluentui/react-icons';
import { useNotificationStore, type Notification, type NotificationType } from '@/stores/notificationStore';
import { getRelativeTime } from '@/utils/dateTime';
import { getNotificationDisplayContent } from '@/utils/primitives';
import { formatValidationErrorType } from '@/utils/validation';
import { customScrollbarStyles, thinScrollbarStyles } from '@/styles/scrollbar';

const useStyles = makeStyles({
  triggerButton: {
    position: 'relative',
    // Match the sibling header icon buttons (search, feedback, docs) so
    // the header action cluster is visually symmetric: 32×32 hit target
    // with 4px horizontal inset, letting Fluent's Button own the vertical
    // metrics. Anchors the absolutely-positioned unread CounterBadge.
    minWidth: '32px',
    height: '32px',
    paddingInline: tokens.spacingHorizontalXS,
    // Fluent's Button root sets `overflow: hidden` (for label ellipsis),
    // which clips the corner badge. This button is icon-only, so nothing
    // relies on that truncation. Scoped to this trigger only.
    overflow: 'visible',
  },
  badge: {
    position: 'absolute',
    top: '0px',
    right: '0px',
    minWidth: '16px',
    height: '16px',
    fontSize: tokens.fontSizeBase100,
  },
  panel: {
    width: '420px',
    maxHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
  },
  headerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    ...customScrollbarStyles,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  notificationItem: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    alignItems: 'flex-start',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  notificationItemUnread: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  iconContainer: {
    flexShrink: 0,
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular,
    marginTop: tokens.spacingVerticalXXS,
  },
  iconSuccess: {
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  iconError: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
  iconWarning: {
    color: tokens.colorPaletteYellowForeground1,
    backgroundColor: tokens.colorPaletteYellowBackground1,
  },
  iconInfo: {
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
  },
  notificationContent: {
    flex: 1,
    minWidth: 0,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  notificationTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalXS,
    display: 'block',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  notificationMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.4',
    display: 'block',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  notificationTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
  notificationActions: {
    display: 'flex',
    alignItems: 'flex-start',
    flexShrink: 0,
    marginTop: '-2px',
  },
  dismissButton: {
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  detailsContainer: {
    marginTop: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    maxHeight: '150px',
    overflowY: 'auto',
    overflowX: 'hidden',
    ...thinScrollbarStyles,
  },
  expandButton: {
    marginTop: tokens.spacingVerticalXS,
    padding: 0,
    minWidth: 'auto',
    height: 'auto',
    fontSize: tokens.fontSizeBase200,
  },
  // Validation errors list
  validationErrorsList: {
    marginTop: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  validationErrorItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusSmall,
    borderLeft: `3px solid ${tokens.colorPaletteRedBorder2}`,
  },
  validationErrorType: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteRedForeground2,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
  },
  validationErrorMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.4',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  validationErrorPath: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
});

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'success':
      return <CheckmarkCircleRegular />;
    case 'error':
      return <ErrorCircleRegular />;
    case 'warning':
      return <WarningRegular />;
    case 'info':
      return <InfoRegular />;
  }
}

function getIconStyle(type: NotificationType, styles: ReturnType<typeof useStyles>) {
  switch (type) {
    case 'success':
      return styles.iconSuccess;
    case 'error':
      return styles.iconError;
    case 'warning':
      return styles.iconWarning;
    case 'info':
      return styles.iconInfo;
  }
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onRemove: (id: string) => void;
}

const NotificationItem = memo(({ notification, onMarkAsRead, onRemove }: NotificationItemProps) => {
  const styles = useStyles();
  const [showDetails, setShowDetails] = useState(false);
  const displayContent = getNotificationDisplayContent(notification);

  // Check if we have multiple validation errors (need expand/collapse)
  const validationErrorCount = notification.validationErrors?.length ?? 0;
  const hasMultipleValidationErrors = validationErrorCount > 1;
  const hasExpandableContent = notification.details || hasMultipleValidationErrors;

  const handleClick = useCallback(() => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    if (hasExpandableContent) {
      setShowDetails((prev) => !prev);
    }
  }, [notification, onMarkAsRead, hasExpandableContent]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(notification.id);
  }, [notification.id, onRemove]);

  return (
    <div
      className={mergeClasses(
        styles.notificationItem,
        !notification.read && styles.notificationItemUnread
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${notification.type} notification: ${displayContent.title ?? displayContent.message}`}
    >
      <div className={mergeClasses(styles.iconContainer, getIconStyle(notification.type, styles))}>
        {getNotificationIcon(notification.type)}
      </div>
      <div className={styles.notificationContent}>
        {displayContent.title && (
          <Text className={styles.notificationTitle}>{displayContent.title}</Text>
        )}
        <Text className={styles.notificationMessage}>{displayContent.message}</Text>
        
        {/* Expand/collapse button for multiple errors or legacy details */}
        {hasExpandableContent && (
          <Button
            appearance="transparent"
            size="small"
            className={styles.expandButton}
            icon={showDetails ? <ChevronUpRegular /> : <ChevronDownRegular />}
            iconPosition="after"
            onClick={(e) => {
              e.stopPropagation();
              setShowDetails((prev) => !prev);
            }}
          >
            {showDetails ? 'Hide details' : `Show ${validationErrorCount} errors`}
          </Button>
        )}

        {/* Validation errors - structured display (only for multiple errors) */}
        {showDetails && hasMultipleValidationErrors && notification.validationErrors && (
          <div className={styles.validationErrorsList}>
            {notification.validationErrors.map((error, index) => {
              const displayType = formatValidationErrorType(error.type);
              return (
                <div key={index} className={styles.validationErrorItem}>
                  {displayType && (
                    <span className={styles.validationErrorType}>
                      {displayType}{error.severity ? ` • ${error.severity}` : ''}
                    </span>
                  )}
                  <span className={styles.validationErrorMessage}>{error.message}</span>
                  {error.path && (
                    <span className={styles.validationErrorPath}>Path: {error.path}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Legacy details - JSON display */}
        {showDetails && notification.details && !notification.validationErrors && (
          <div className={styles.detailsContainer}>
            {notification.details}
          </div>
        )}

        <Text className={styles.notificationTime}>{getRelativeTime(new Date(notification.timestamp))}</Text>
      </div>
      <div className={styles.notificationActions}>
        <Tooltip content="Dismiss notification" relationship="label">
          <Button
            appearance="transparent"
            size="small"
            icon={<DismissRegular />}
            onClick={handleRemove}
            aria-label="Dismiss notification"
            className={styles.dismissButton}
          />
        </Tooltip>
      </div>
    </div>
  );
});

export const NotificationPanel = () => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  
  const notifications = useNotificationStore((state) => state.notifications);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const markAsRead = useNotificationStore((state) => state.markAsRead);
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead);
  const removeNotification = useNotificationStore((state) => state.removeNotification);
  const clearAllNotifications = useNotificationStore((state) => state.clearAllNotifications);

  // Focus management: focus header when panel opens
  useEffect(() => {
    if (isOpen && headerRef.current) {
      headerRef.current.focus();
    }
  }, [isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          className={styles.triggerButton}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          icon={isOpen ? <AlertFilled /> : <AlertRegular />}
        >
          {/* aria-hidden because the button's aria-label already announces
              the unread count. */}
          {unreadCount > 0 && (
            <CounterBadge
              className={styles.badge}
              count={unreadCount}
              overflowCount={9}
              size="small"
              appearance="filled"
              color="danger"
              aria-hidden="true"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverSurface className={styles.panel}>
        <div className={styles.header} ref={headerRef} tabIndex={-1}>
          <Text className={styles.headerTitle}>Notifications</Text>
          <div className={styles.headerActions}>
            {unreadCount > 0 && (
              <Button
                appearance="subtle"
                size="small"
                onClick={markAllAsRead}
              >
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Tooltip content="Clear all notifications" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DeleteRegular />}
                  onClick={clearAllNotifications}
                  aria-label="Clear all notifications"
                />
              </Tooltip>
            )}
          </div>
        </div>
        <div className={styles.list}>
          {notifications.length === 0 ? (
            <div className={styles.emptyState}>
              <AlertRegular fontSize={32} />
              <Text>No notifications</Text>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onRemove={removeNotification}
              />
            ))
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
};
